import { Button, Input, Row, Col, Container } from "reactstrap";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useMemo, useRef, useEffect, useState } from "react";
import { Whisper, Popover } from "rsuite";

import ViewRecordDrawer from "../../../components/Drawers/Records/ViewRecordDrawer";
import FieldFormatter from "../../../components/Records/Formatters/FieldFormatter";
import useUpdateRecord from "../../../api/api-hooks/records/useUpdateRecord";
import { AppToggle } from "../../../components/Drawers/Fields/CustomToggle";
import useUserAccounts from "../../../api/api-hooks/auth/useUserAccounts";
import RecordNameCell from "../../../components/Records/RecordNameCell";
import useUpdateUser from "../../../api/api-hooks/auth/useUpdateUser";
import useRecord from "../../../api/api-hooks/records/useRecord";
import UppyFile from "../../../components/Inputs/UppyFile";
import useUser from "../../../api/api-hooks/auth/useUser";
import { newToastEvent } from "../../../utils/ToastUtil";
import AppSpinner from "../../../elements/AppSpinner";
import Phone from "../../../components/Inputs/Phone";
import useModule from "../../../hooks/useModule";

const inputStyles = { width: "100%", maxWidth: 400 };

const AccountProfile = ({ tenantID }: { tenantID: string | undefined }) => {
  const { user, userLoading } = useUser(true);
  const { userAccounts, userAccountsLoading } = useUserAccounts();
  const { updateUser, userUpdating, updateUserError } = useUpdateUser();
  const { module } = useModule("Contacts", true);
  const { record, recordLoading } = useRecord({
    moduleAPIName: "Contacts",
    recordID: user?.id,
  }); // Not Fetch when tenantID not provided.
  const [open, setOpen] = useState(false);
  const uploadRef = useRef<any>();
  const { updateRecord } = useUpdateRecord(false, "");
  let primaryCompanyField = module?.Fields?.find(
    (f: any) => f.APIName === "Company"
  );
  let companiesField = module?.Fields?.find(
    (f: any) => f.APIName === "Companies"
  );
  let profileImageField = module?.Fields?.find(
    (f: any) => f.APIName === "ProfileImage"
  );

  // Form State
  const [name, setName] = useState(user?.Name ?? "");
  const [email, setEmail] = useState(user?.PrimaryContactEmail ?? "");
  const [phone, setPhone] = useState(user?.Phone ?? "");
  const [impersonateStatus, setImpersonateStatus] = useState(
    user?.Impersonate ?? false
  );
  const [editPicturePage, setEditPicturePage] = useState(false);
  const [keepOpen, setKeepOpen] = useState(false);

  const uniqueEmails = useMemo(() => {
    const emailSet = new Set(userAccounts?.map((account) => account.Email));
    const uniqueEmails = Array.from(emailSet);
    return uniqueEmails;
  }, [userAccounts]);

  // Sync state with user information updates
  useEffect(() => {
    if (!name) setName(user?.Name ?? "");
    if (!email) setEmail(user?.PrimaryContactEmail ?? "");
    if (!phone) setPhone(user?.Phone ?? "");
    if (!impersonateStatus) setImpersonateStatus(user?.Impersonate ?? false);
  }, [
    user?.Name,
    user?.PrimaryContactEmail,
    user?.Phone,
    user?.Impersonate,
    userLoading,
  ]);

  // Respond to error state updates
  useEffect(() => {
    if (!updateUserError) return;

    newToastEvent("error", "An error occurred. Please try again.");
  }, [updateUserError]);

  // Update the user's profile (Current User's Person Object in Azure.)
  const updateProfile = () => {
    if (!name || !email || !phone) {
      return newToastEvent("error", "Missing required field!");
    }
    if (!isValidPhoneNumber(phone)) {
      return newToastEvent("error", "Invalid phone number");
    }

    const payload: any = {};

    if (phone !== user.Phone) {
      payload.Phone = phone;
    }
    if (name !== user.Name) {
      payload.Name = name;
    }
    if (email !== user.PrimaryContactEmail) {
      payload.PrimaryContactEmail = email;
    }

    if (impersonateStatus !== user.Impersonate) {
      payload.Impersonate = impersonateStatus;
    }

    updateUser({
      userID: user?.id,
      payload: payload,
      onSuccess: () => newToastEvent("success", "Successfully updated profile"),
    });
  };

  const updateFile = (file: any) => {
    updateRecord({
      field: profileImageField,
      recordID: record?.id,
      payload: {
        [profileImageField?.APIName ?? "ProfileImage"]: file,
      },
      moduleAPIName: "Contacts",
      _UserActivity: null,
      originalRecord: record,
    });
  };

  return (
    <Container fluid>
      <div className="px-3">
        <h3>Contact Information</h3>
        <hr className="mt-2 mb-3"></hr>
        <Row>
          {tenantID && (
            <Col xs={12} sm="auto" className="ml-4 mb-4">
              <h5>Profile Picture</h5>
              <div
                style={{
                  position: "relative",
                  height: 175,
                  width: 175,
                  borderRadius: "50%",
                }}
                className="d-flex justify-content-center border border-medium"
              >
                {recordLoading ? (
                  SkeletonImageLoader
                ) : !!record?.ProfileImage?.PublicURL ? (
                  <img
                    src={record?.ProfileImage?.PublicURL}
                    style={{
                      width: 175,
                      height: 175,
                      borderRadius: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <i
                    className="mdi mdi-account text-dark"
                    style={{ fontSize: 110 }}
                  />
                )}

                <Whisper
                  placement="autoHorizontalStart"
                  trigger={!keepOpen ? "click" : "none"}
                  ref={uploadRef}
                  onClose={() => setEditPicturePage(false)}
                  speaker={
                    <Popover
                      className={`sidebar-popover ${
                        keepOpen ? "tw:hidden" : ""
                      } tw:p-3`}
                      onClick={(e: any) => e.stopPropagation()}
                    >
                      {!editPicturePage && record?.ProfileImage?.PublicURL && (
                        <>
                          <div
                            className=" text-sm rounded-sm shadow-sm m-1 p-1 font-weight-600 border border-superlight d-flex align-items-center cursor-pointer bg-superlight--hover"
                            onClick={() => setEditPicturePage(true)}
                          >
                            <i className="mdi mdi-upload ml-1 mr-2" />
                            Upload a photo
                          </div>
                          <div
                            className="text-sm rounded-sm shadow-sm m-1 p-1 font-weight-600 border border-superlight d-flex align-items-center cursor-pointer bg-superlight--hover"
                            onClick={() => {
                              updateFile(null);
                            }}
                          >
                            <i className="mdi mdi-close ml-1 text-danger mr-2" />{" "}
                            Remove photo
                          </div>
                        </>
                      )}
                      {(!record?.ProfileImage?.PublicURL ||
                        editPicturePage) && (
                        <UppyFile
                          fileType="Image"
                          width={300}
                          meta={{ isPublic: true }}
                          multiFiles={false}
                          afterSubmit={(files) => {
                            updateFile(files);
                          }}
                          filesToFilter={
                            record?.ProfileImage ? [record?.ProfileImage] : []
                          }
                          setKeepOpen={setKeepOpen}
                        />
                      )}
                    </Popover>
                  }
                >
                  <div
                    className="d-flex"
                    style={{
                      position: "absolute",
                      bottom: 0,
                      left: 0,
                      padding: "1px 6px",
                    }}
                  >
                    <Button
                      style={{
                        position: "absolute",
                        bottom: 12,
                        left: 0,
                        padding: "1px 6px",
                      }}
                    >
                      <div className="d-flex">
                        <i className="mdi mdi-pencil mr-1"></i>
                        Edit
                      </div>
                    </Button>
                  </div>
                </Whisper>
              </div>
            </Col>
          )}
          <Col xs={9}>
            <h5>Name</h5>
            <Input
              bsSize="sm"
              value={name}
              className="mb-4"
              style={inputStyles}
              onChange={(e) => setName(e.target.value)}
            />
            <h5>Primary Email</h5>
            <Input
              bsSize="sm"
              type="select"
              className="mb-4"
              value={email}
              style={inputStyles}
              onChange={(e) => setEmail(e.target.value)}
            >
              {!userAccountsLoading &&
                uniqueEmails?.map((account, idx) => (
                  <option key={idx} value={account}>
                    {account}
                  </option>
                ))}
            </Input>

            {tenantID && record && !recordLoading && (
              <>
                <h5 className="mb--1">My GrowthCloud Contact</h5>
                <small className="text-warning">
                  This contact is linked to directly to your account.
                </small>
                <div className="d-flex align-items-center pb-2 mt-1">
                  <RecordNameCell
                    record={record}
                    onOpen={() => {
                      setOpen(true);
                    }}
                    fontSize={13}
                    metadataFieldType={null}
                    maxWidth={259}
                    // @ts-ignore
                    tooltipPlacement={"autoVertical"}
                    className="pr-1"
                  />
                  <img
                    className="ml-1"
                    src="/img/GCLogo512x512.png"
                    style={{
                      height: 22,
                      width: 22,
                      marginTop: -1,
                    }}
                  />
                </div>
                {primaryCompanyField && (
                  <>
                    <h5 className="mb-0 mt-2">Default Organization</h5>
                    <div className="w-100" style={{ fontSize: "13px" }}>
                      {/** @ts-ignore */}
                      <FieldFormatter
                        field={primaryCompanyField}
                        value={record[primaryCompanyField?.APIName]}
                        record={record}
                        recordID={record?._id || record?.id}
                        forceReadOnly={false}
                        moduleAPIName={module?.APIName}
                        subitemParentID={null}
                      />
                    </div>
                  </>
                )}
                {companiesField && (
                  <>
                    <h5 className="mb-0 mt-2">
                      Other Organizations Associated with
                    </h5>
                    <div className="w-100" style={{ fontSize: "14px" }}>
                      {/** @ts-ignore */}
                      <FieldFormatter
                        field={companiesField}
                        value={record[companiesField?.APIName]}
                        record={record}
                        recordID={record?._id || record?.id}
                        moduleAPIName={module?.APIName}
                        forceReadOnly={false}
                        subitemParentID={null}
                      />
                    </div>
                  </>
                )}
              </>
            )}
            <h5 className="mt-1">Phone Number</h5>
            <div style={inputStyles}>
              <Phone
                existingValue={phone}
                onChange={(val) => setPhone(val)}
                inputSize="sm"
                disabled={true}
              />
            </div>
            <h5 className="mt-3 mb-0">Support Assist Mode</h5>
            <div className="d-flex flex-column">
              <small className="text-warning pb-2">
                Give the GrowthCloud Support team temporary access to my cloud
              </small>
              <AppToggle
                value={impersonateStatus}
                handleChange={(e) => setImpersonateStatus(e.target.checked)}
              />
            </div>
            <Button
              style={{ width: 125 }}
              size="sm"
              color="primary"
              // Disable button if no fields have changed.
              disabled={
                userAccountsLoading ||
                userUpdating ||
                (email === user?.PrimaryContactEmail &&
                  name === user?.Name &&
                  phone === user?.Phone &&
                  impersonateStatus === (user?.Impersonate ?? false))
              }
              className="mt-4 mb-5"
              onClick={updateProfile}
            >
              {userUpdating ? <AppSpinner color="white" /> : "Update Profile"}
            </Button>
          </Col>
        </Row>
      </div>
      <ViewRecordDrawer
        open={open}
        onClose={() => setOpen(false)}
        module={module!}
        recordID={record?.id}
      />
    </Container>
  );
};

const SkeletonImageLoader = (
  <div
    className="skeleton"
    style={{ width: 173, height: 173, borderRadius: "100%" }}
  ></div>
);

export default AccountProfile;
